// ============================================================================
// components/remote/RemoteFilesPanel.tsx — файловый менеджер УСТРОЙСТВА ХОСТА
// (Ф3, viewer): навигация по каталогам, скачивание/загрузка/удаление файлов
// через useRemoteFiles (data-канал → Wails-мост на хосте).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Download, File as FileIcon, Folder, FolderOpen, Loader2, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import { confirmDialog } from '@/lib/dialogs';
import { bytesToB64, formatSize, saveRemoteFile, type HostFsEntry } from '@/lib/remote/remoteFiles';
import type { RemoteFilesApi } from '@/components/remote/useRemoteFiles';

interface Props {
  files: RemoteFilesApi;
  onClose: () => void;
}

export function RemoteFilesPanel({ files, onClose }: Props) {
  const [path, setPath] = useState('');
  const [parent, setParent] = useState('');
  const [entries, setEntries] = useState<HostFsEntry[]>([]);
  const [roots, setRoots] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (p: string) => {
    const r = await files.list(p);
    if (!r) return;
    setPath(r.path);
    setParent(r.parent);
    setEntries(r.entries as unknown as HostFsEntry[]);
  }, [files]);

  useEffect(() => {
    (async () => {
      const r = await files.roots();
      setRoots(r);
      const first = r[0] || '';
      if (first) await load(first);
    })();
    // загрузка один раз при открытии панели
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEntry = (e: HostFsEntry) => {
    if (e.is_dir) void load(joinPath(path, e.name));
  };

  const download = async (e: HostFsEntry) => {
    const full = joinPath(path, e.name);
    const r = await files.getFile(full);
    if (r) await saveRemoteFile(r.name, r.data);
  };

  const upload = async (f: File) => {
    if (!path) return;
    const buf = new Uint8Array(await f.arrayBuffer());
    const ok = await files.putFile(joinPath(path, f.name), bytesToB64(buf));
    if (ok) await load(path);
  };

  const remove = async (e: HostFsEntry) => {
    const ok = await confirmDialog(`Удалить файл «${e.name}» на устройстве хоста?`, 'Удаление файла');
    if (!ok) return;
    const done = await files.remove(joinPath(path, e.name));
    if (done) await load(path);
  };

  return (
    <div className="absolute inset-y-0 right-0 z-[12600] flex w-[430px] max-w-[92vw] flex-col border-l border-white/10 bg-black/80 backdrop-blur-md">
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
        <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">Файлы устройства (host)</span>
        {files.busy && <Loader2 className="h-4 w-4 animate-spin text-white/60" />}
        <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-white/10" aria-label="Закрыть">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
        <button
          type="button"
          disabled={!parent}
          onClick={() => parent && void load(parent)}
          className="rounded-md p-1.5 hover:bg-white/10 disabled:opacity-30"
          title="Вверх"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
        <input
          value={path}
          onChange={(ev) => setPath(ev.target.value)}
          onKeyDown={(ev) => { if (ev.key === 'Enter') void load(path); }}
          className="min-w-0 flex-1 rounded-md border border-white/15 bg-black/40 px-2 py-1 text-xs outline-none focus:border-primary"
          placeholder="Путь…"
        />
        <button type="button" onClick={() => void load(path)} className="rounded-md p-1.5 hover:bg-white/10" title="Обновить">
          <RefreshCw className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-md p-1.5 hover:bg-white/10" title="Загрузить файл на хост">
          <Upload className="h-4 w-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(ev) => {
            const f = ev.target.files?.[0];
            if (f) void upload(f);
            ev.target.value = '';
          }}
        />
      </div>

      {roots.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b border-white/10 px-3 py-2">
          {roots.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => void load(r)}
              className={`rounded-md px-2 py-1 text-[11px] ${path.startsWith(r) ? 'bg-primary/30 text-white' : 'bg-white/10 hover:bg-white/20'}`}
            >
              {r}
            </button>
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {entries.length === 0 && !files.busy && (
          <div className="flex h-full items-center justify-center text-xs italic text-white/40">Пусто</div>
        )}
        {entries.map((e) => (
          <div
            key={e.name}
            className="group flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-white/5"
          >
            {e.is_dir ? <Folder className="h-4 w-4 shrink-0 text-amber-300" /> : <FileIcon className="h-4 w-4 shrink-0 text-white/50" />}
            <button
              type="button"
              onClick={() => openEntry(e)}
              className={`min-w-0 flex-1 truncate text-left ${e.is_dir ? 'cursor-pointer hover:text-primary' : ''}`}
              title={e.name}
            >
              {e.name}
            </button>
            <span className="shrink-0 text-[11px] text-white/40">{e.is_dir ? '' : formatSize(e.size)}</span>
            {!e.is_dir && (
              <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button type="button" onClick={() => void download(e)} className="rounded p-1 hover:bg-white/10" title="Скачать на моё устройство">
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => void remove(e)} className="rounded p-1 text-red-300 hover:bg-white/10" title="Удалить на хосте">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </div>
        ))}
      </div>

      {files.error && (
        <div className="border-t border-red-500/30 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">{files.error}</div>
      )}
    </div>
  );
}

function joinPath(dir: string, name: string): string {
  if (!dir) return name;
  const sep = dir.includes('\\') ? '\\' : '/';
  return dir.endsWith(sep) ? `${dir}${name}` : `${dir}${sep}${name}`;
}
